<script setup lang="ts">
/*
 * Hierarchical scope switcher in the top bar (V2-FE-1, re-architected
 * V2-FE-switcher-list-employers, visually refined V2-FE-switcher-hierarchy-ui).
 * Renders the scope hierarchy as a TREE rooted at the tenant (outsourcing)
 * company, grouped in-app from the single list-all-employers endpoint:
 *
 *   🌐 All Scopes                              oversight   ← global (TenantAdmin only)
 *   COMPANY
 *   🏢 <Tenant employer>                          company  ← Type=TENANT_EMPLOYER (root)
 *      ┊ DIRECT · 👥 Direct Workforce                      ← the only "Direct" entry (QA D6)
 *      ┊ 🏢 <company>                                       ← Kind=COMPANY subsidiary
 *      ┊ CLIENTS ▸ PROJECTS
 *      ┊ 💼 <client>                                        ← OS_CLIENT (ParentId=company)
 *      ┊    ┊ 📁 <project>                                  ← OS_PROJECT (ParentId=client)
 *      ┊ VENDORS ▸ PROJECTS
 *      ┊ 💼 <vendor>                                        ← OS_VENDOR (ParentId=company)
 *      ┊    ┊ 📁 <project>
 *
 * Clients and vendors are counterparties OF the tenant company, so they render
 * INDENTED beneath it (one level), and their projects one level deeper — every
 * parent→child edge draws a dotted tree connector. Both Clients and Vendors
 * groups show whenever each kind has ≥1 visible entry, regardless of persona
 * (owner decision "Option A").
 *
 * Iconography (lucide): Globe=All Scopes, Building2=company + subsidiaries,
 * Briefcase=client/vendor counterparty, Folder=OS_PROJECT.
 *
 * Show-more: any parent with >5 children renders the first 5 then a "Show more
 * (N)" toggle (projects under a counterparty; subsidiaries / clients / vendors
 * within the company). Expand state is tracked locally in a `Set` keyed by
 * parent id (synthetic keys for the group-level lists).
 *
 * Role-based listing (handled in `deriveScopeListGroups`): TenantAdmin sees the
 * global option + every employer; otherwise only employers whose id is in the
 * `administered_companies` claim (no global option).
 *
 * Chip face is two lines: the selected scope's label — with the tenant OrgId in
 * parentheses when a concrete company is selected — over a muted context
 * subtitle. The panel is a Headless UI Menu; selecting any node sets the active
 * scope, invalidates every module query, and warm-prefetches visited screens.
 *
 * Direct Workforce (plan X1, proto `renderScopeMenu` L15626–15630): every
 * COMPANY-kind row carries a "Direct" caption + a "Direct Workforce" item as its
 * first child (`OrgChipDirectItem`). Selecting it sets the store's
 * `{ scope: 'direct', employerId: <company> }`; the chip then reads "Direct" over
 * "<company> · own workforce" (proto `updateScopeUI`, L15590–15591).
 *
 * Data + selection logic live in `useScopeTree()`; this component is chrome.
 */
import { computed, reactive, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useQueryClient } from '@tanstack/vue-query';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { Briefcase, Building2, Check, ChevronDown, Folder, Globe, Plus } from 'lucide-vue-next';

import { useAuthStore } from '@/stores/auth';
import { useOrgStore } from '@/stores/org';
import { useScopeTree } from '@/composables/useScopeTree';
import { useI18n } from 'vue-i18n';
import { Drawer } from '@/ui';
import { useToast } from '@/composables/useToast';
import EmployerForm from '@/modules/organization/components/EmployerForm.vue';
import {
  createEmployer,
  employerFullName,
  employerInitial,
  employerLabel,
  employerShortBadge,
  type Employer,
  type EmployerRequest,
} from '@/api/services/employers';
import { refusalText, parentCandidates } from '@/api/services/employerLifecycle';
import OrgChipCounterparty from './OrgChipCounterparty.vue';
import OrgChipDirectItem from './OrgChipDirectItem.vue';
import './org-chip-tree.css';

/** Children past this count collapse behind a "Show more (N)" toggle. */
const MAX_VISIBLE_CHILDREN = 5;

const auth = useAuthStore();
const orgStore = useOrgStore();
const { t } = useI18n();
const toast = useToast();
const queryClient = useQueryClient();
const { activeEmployerId, isGlobalScope, isDirectScope, directEmployerId } = storeToRefs(auth);
const { orgId: tenantOrgId } = storeToRefs(orgStore);
const {
  groups,
  flattened,
  canSelectGlobal,
  isLoading,
  selectEmployerScope,
  selectGlobal,
  selectDirect,
} = useScopeTree();

// --- Create-company action ------------------------------------------------
// Only tenant-wide admins may create a company (they are the principals who can
// see every employer + the global scope). `canSelectGlobal` is exactly the
// `isTenantAdmin` predicate already resolved by `useScopeTree`, so reuse it as
// the gate rather than re-reading the claim.
const canCreateCompany = canSelectGlobal;
const createOpen = ref(false);
const saving = ref(false);
/** The server's refusal of the last create, shown inside the drawer (R3-W1). */
const createError = ref<string | null>(null);

// R3-W1: the create form's Parent Select (V1 W3-E) — every COMPANY-kind
// employer, defaulting to the tenant root. Creating is TenantAdmin-only, and an
// admin's `flattened` list is the whole tenant.
const createParentOptions = computed(() => parentCandidates(flattened.value, null));
const createDefaultParentId = computed(() => groups.value.tenantEmployer?.id ?? null);

function openCreate(): void {
  createError.value = null;
  createOpen.value = true;
}

/** Submit a new COMPANY employer, refresh the switcher, and select it. */
async function submitCreateCompany(body: EmployerRequest): Promise<void> {
  saving.value = true;
  createError.value = null;
  try {
    const created = await createEmployer({ ...body, Kind: body.Kind ?? 'COMPANY' });
    // Refetch the tenant-wide employer list the switcher reads from so the new
    // company appears immediately, then switch scope to it.
    await queryClient.invalidateQueries({ queryKey: ['scope-employers'] });
    selectEmployerScope(created);
    toast.success(t('v2.organization.company-form.toasts.created', { name: created.name }));
    createOpen.value = false;
  } catch (err) {
    // Show the server's own reason inline (e.g. an ID format containing ':');
    // keep the generic toast for parity with the pre-R3 behaviour.
    createError.value = refusalText(err) ?? null;
    toast.error(t('v2.organization.company-form.toasts.create-failed'));
  } finally {
    saving.value = false;
  }
}

const selected = computed<Employer | null>(() => {
  const id = activeEmployerId.value;
  if (!id) return null;
  return flattened.value.find((e) => e.id === id) ?? null;
});

const tenantEmployer = computed(() => groups.value.tenantEmployer);
const companies = computed(() => groups.value.companies);
// Both counterparty groups render whenever they have ≥1 visible entry,
// regardless of tenant persona (owner decision "Option A").
const clients = computed(() => groups.value.clients);
const vendors = computed(() => groups.value.vendors);
// Administered projects whose parent client/vendor isn't itself visible —
// rendered flat, like `companies`, since there's no visible parent to nest
// them under (a project-scoped admin, or a legacy pre-ParentId project row).
const looseProjects = computed(() => groups.value.looseProjects);
// Placement honours ParentId: subsidiaries carry the counterparties parented to
// them; `tenant*` are the counterparties parented to the tenant employer.
const subsidiaryNodes = computed(() => groups.value.subsidiaryNodes);
const tenantClients = computed(() => groups.value.tenantClients);
const tenantVendors = computed(() => groups.value.tenantVendors);

/**
 * The selected employer id for highlighting (null in global scope, and under a
 * Direct Workforce selection — there the Direct item carries the tick, not the
 * company row).
 */
const activeId = computed<string | null>(() =>
  isGlobalScope.value || isDirectScope.value ? null : (selected.value?.id ?? null),
);

const hasAnyScope = computed(
  () =>
    tenantEmployer.value !== null ||
    companies.value.length > 0 ||
    clients.value.length > 0 ||
    vendors.value.length > 0 ||
    looseProjects.value.length > 0,
);

// --- Chip face ------------------------------------------------------------
// Two-line collapsed button: avatar initial + bold label (+ tenant OrgId in
// parens when a company is selected) over a muted context subtitle.
const chipLabel = computed(() => {
  if (isGlobalScope.value) return t('v2.shell.org-chip.global-short');
  // Proto `updateScopeUI`: the Direct scope's label is just "Direct".
  if (isDirectScope.value) return t('v2.shell.org-chip.direct-short');
  const sel = selected.value;
  return sel ? employerLabel(sel) : t('v2.shell.org-chip.switch');
});
const subId = computed(() => tenantOrgId.value ?? '');
/** Show the tenant OrgId (in parens) only when a concrete company is in scope. */
const showOrgId = computed(
  () =>
    !isGlobalScope.value && !isDirectScope.value && subId.value !== '' && selected.value !== null,
);
const chipSubtitle = computed(() => {
  if (isGlobalScope.value) return t('v2.shell.org-chip.oversight');
  const sel = selected.value;
  // Proto `updateScopeUI`: "<company> · own workforce".
  if (isDirectScope.value) {
    return t('v2.shell.org-chip.direct-sub', { company: sel ? labelOf(sel) : '' });
  }
  if (!sel) return '';
  // Project → its parent counterparty's label; counterparty → "Client"/"Vendor".
  if (sel.kind === 'OS_PROJECT') {
    const parent = parentLabelFor(sel);
    return parent || t('v2.shell.org-chip.context-project');
  }
  if (sel.kind === 'OS_CLIENT') return t('v2.shell.org-chip.context-client');
  if (sel.kind === 'OS_VENDOR') return t('v2.shell.org-chip.context-vendor');
  // A company (the tenant root or a subsidiary) is oversight of its own units, NOT
  // "own workforce" — that phrase belongs to Direct Workforce only (QA D6). Proto
  // `updateScopeUI` (L15591): "<company> · company".
  return t('v2.shell.org-chip.company-sub', { company: labelOf(sel) });
});
const initial = computed(() => {
  if (isGlobalScope.value) return '*';
  return selected.value ? employerInitial(selected.value) : 'S';
});

/** Resolve a project's owning counterparty label (for the chip subtitle). */
function parentLabelFor(project: Employer): string {
  const all = [...clients.value, ...vendors.value];
  const owner = all.find((entry) => entry.employer.id === project.parentId)?.employer;
  return owner ? employerLabel(owner) : '';
}

/**
 * Primary row label — the FULL company/client/vendor/project name so the user
 * recognises each entry at a glance. Falls back through the short name / id so
 * it never renders '?' / '—'.
 */
function labelOf(e: Employer): string {
  return employerFullName(e);
}

/**
 * The small short-name badge shown in grey beneath the full name. Empty when
 * the short name is missing or identical to the full name (no point repeating).
 */
function shortBadgeOf(e: Employer): string {
  return employerShortBadge(e);
}

function onSelectEmployer(e: Employer) {
  selectEmployerScope(e);
}

function onSelectGlobal() {
  selectGlobal();
}

function isActive(id: string): boolean {
  return !isGlobalScope.value && !isDirectScope.value && selected.value?.id === id;
}

/** `true` when `companyId`'s Direct Workforce is the active scope. */
function isDirectActive(companyId: string): boolean {
  return isDirectScope.value && directEmployerId.value === companyId;
}

function onSelectDirect(company: Employer) {
  selectDirect(company);
}

// --- Show-more state ------------------------------------------------------
// Locally tracked expanded parent ids. A parent is keyed by its own id; the
// three group-level lists (subsidiaries / clients-roots / vendors-roots) use
// the synthetic keys below so they never collide with a real employer id.
const COMPANIES_KEY = '__companies__';
const CLIENTS_KEY = '__clients__';
const VENDORS_KEY = '__vendors__';
const LOOSE_PROJECTS_KEY = '__loose_projects__';
const expanded = reactive(new Set<string>());

function isExpanded(key: string): boolean {
  return expanded.has(key);
}
function toggleExpanded(key: string): void {
  if (expanded.has(key)) expanded.delete(key);
  else expanded.add(key);
}
/** Cap a list to the first N items unless its parent key is expanded. */
function visibleSlice<T>(items: T[], key: string): T[] {
  if (items.length <= MAX_VISIBLE_CHILDREN || expanded.has(key)) return items;
  return items.slice(0, MAX_VISIBLE_CHILDREN);
}
/** How many items a "Show more (N)" toggle would reveal (0 → no toggle). */
function hiddenCount(items: { length: number }, key: string): number {
  if (items.length <= MAX_VISIBLE_CHILDREN || expanded.has(key)) return 0;
  return items.length - MAX_VISIBLE_CHILDREN;
}

// Capped views of each group's top-level rows.
const visibleCompanies = computed(() => visibleSlice(companies.value, COMPANIES_KEY));
const visibleLooseProjects = computed(() => visibleSlice(looseProjects.value, LOOSE_PROJECTS_KEY));
const visibleClients = computed(() => visibleSlice(clients.value, CLIENTS_KEY));
// Case A (tree) capped views: subsidiary nodes + the tenant's own counterparties.
const visibleSubsidiaries = computed(() => visibleSlice(subsidiaryNodes.value, COMPANIES_KEY));
const visibleTenantClients = computed(() => visibleSlice(tenantClients.value, CLIENTS_KEY));
const visibleTenantVendors = computed(() => visibleSlice(tenantVendors.value, VENDORS_KEY));
</script>

<template>
  <!-- min-w-0 + shrink: at narrow widths the chip truncates instead of pushing the top bar
       past the viewport (Q13 / D2). At desktop the nav pills absorb the free space first. -->
  <Menu as="div" class="relative min-w-0 shrink" data-testid="org-chip">
    <div class="inline-flex min-w-0 max-w-full">
      <MenuButton
        class="org-chip min-w-0 max-w-full"
        :title="t('v2.shell.org-chip.switch')"
        :aria-label="t('v2.shell.org-chip.switch')"
        data-testid="org-chip-button"
      >
        <span class="o-ico shrink-0" aria-hidden="true">{{ initial }}</span>
        <span class="block min-w-0 text-left leading-tight">
          <span class="block truncate font-semibold text-app-fg" data-testid="org-chip-short-name">
            {{ chipLabel
            }}<span v-if="showOrgId" class="font-normal text-app-muted">
              (<span data-testid="org-chip-tenant-id">{{ subId }}</span
              >)</span
            >
          </span>
          <span
            v-if="chipSubtitle"
            class="block truncate pr-1 text-[11px] font-normal text-app-muted"
            data-testid="org-chip-subtitle"
          >
            {{ chipSubtitle }}
          </span>
        </span>
        <ChevronDown class="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
      </MenuButton>
    </div>
    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <MenuItems
        class="absolute left-0 z-50 mt-2 max-h-[70vh] w-80 origin-top-left overflow-y-auto rounded-xl border border-app-border bg-app-surface p-1 shadow-popup focus:outline-none"
        data-testid="org-chip-menu"
      >
        <div
          v-if="isLoading"
          class="px-3 py-2 text-md text-app-muted"
          data-testid="org-chip-loading"
        >
          {{ t('v2.shell.org-chip.loading') }}
        </div>
        <template v-else>
          <!-- All Scopes (global) — top-level admins only. Globe + right-aligned
               muted "oversight" text per the design. -->
          <MenuItem v-if="canSelectGlobal" v-slot="{ active }">
            <button
              type="button"
              :class="[
                'group flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left transition-colors',
                active ? 'bg-slate-50' : '',
              ]"
              data-testid="org-chip-option-global"
              @click="onSelectGlobal"
            >
              <span class="oc-icon oc-icon--brand" aria-hidden="true">
                <Globe class="h-4 w-4" />
              </span>
              <span class="min-w-0 flex-1 truncate text-md font-semibold text-app-fg">
                {{ t('v2.shell.org-chip.global-label') }}
              </span>
              <span
                v-if="!isGlobalScope"
                class="shrink-0 text-[11px] text-app-muted"
                data-testid="org-chip-oversight"
              >
                {{ t('v2.shell.org-chip.oversight') }}
              </span>
              <Check
                v-if="isGlobalScope"
                class="ml-auto h-4 w-4 shrink-0 text-primary"
                aria-hidden="true"
              />
            </button>
          </MenuItem>

          <div
            v-if="!hasAnyScope"
            class="px-3 py-2 text-md text-app-muted"
            data-testid="org-chip-empty"
          >
            {{ t('v2.shell.org-chip.empty') }}
          </div>

          <!-- =====================================================================
               CASE A — the tenant (outsourcing) company is visible: render the
               whole hierarchy as a tree ROOTED at it. Subsidiaries, clients, and
               vendors all nest one level beneath it; projects nest one level
               deeper. Every parent→child edge draws a dotted connector.
               ===================================================================== -->
          <template v-if="tenantEmployer">
            <!-- The root is the COMPANY (oversight of its units), not "Direct": Direct
                 Workforce is the only "Direct" entry (QA D6, proto `renderScopeMenu`
                 L15617–15620 — company row + "company" tag). -->
            <p class="oc-group-header" data-testid="org-chip-group-company">
              {{ t('v2.shell.org-chip.company-group') }}
            </p>
            <!-- Root: the tenant employer (level 0). -->
            <MenuItem v-slot="{ active }">
              <button
                type="button"
                :class="[
                  'group flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left transition-colors',
                  isActive(tenantEmployer.id) ? 'bg-primary-50' : active ? 'bg-slate-50' : '',
                ]"
                :data-testid="`org-chip-option-${tenantEmployer.id}`"
                @click="onSelectEmployer(tenantEmployer)"
              >
                <span class="oc-icon" aria-hidden="true">
                  <Building2 class="h-4 w-4" />
                </span>
                <span class="flex min-w-0 flex-1 flex-col leading-tight">
                  <span
                    :class="[
                      'truncate text-md font-semibold',
                      isActive(tenantEmployer.id) ? 'text-primary-700' : 'text-app-fg',
                    ]"
                  >
                    {{ labelOf(tenantEmployer) }}
                  </span>
                  <span
                    v-if="shortBadgeOf(tenantEmployer)"
                    class="truncate text-[11px] font-normal text-app-muted"
                  >
                    {{ shortBadgeOf(tenantEmployer) }}
                  </span>
                </span>
                <Check
                  v-if="isActive(tenantEmployer.id)"
                  class="ml-auto h-4 w-4 shrink-0 text-primary"
                  aria-hidden="true"
                />
                <span
                  v-else
                  class="shrink-0 text-[11px] text-app-muted"
                  data-testid="org-chip-company-tag"
                >
                  {{ t('v2.shell.org-chip.company-tag') }}
                </span>
              </button>
            </MenuItem>

            <!-- The company's children: subsidiaries, then Clients, then Vendors
                 — all on one dotted guide (level 1). -->
            <div class="oc-children" data-testid="org-chip-children-direct">
              <!-- Direct Workforce of the tenant company (proto L15626–15630). -->
              <OrgChipDirectItem
                :company-id="tenantEmployer.id"
                :selected="isDirectActive(tenantEmployer.id)"
                @select="onSelectDirect(tenantEmployer)"
              />
              <!-- Subsidiaries (Kind=COMPANY). A subsidiary that owns
                   counterparties (by their ParentId) expands them one level
                   deeper; an empty subsidiary is a plain leaf row. -->
              <template v-for="node in visibleSubsidiaries" :key="node.employer.id">
                <MenuItem v-slot="{ active }">
                  <button
                    type="button"
                    :class="[
                      'oc-child-row',
                      isActive(node.employer.id) ? 'bg-primary-50' : active ? 'bg-slate-50' : '',
                    ]"
                    :data-testid="`org-chip-option-${node.employer.id}`"
                    @click="onSelectEmployer(node.employer)"
                  >
                    <span class="oc-icon oc-icon--sm" aria-hidden="true">
                      <Building2 class="h-4 w-4" />
                    </span>
                    <span class="flex min-w-0 flex-1 flex-col leading-tight">
                      <span
                        :class="[
                          'truncate text-sm font-medium',
                          isActive(node.employer.id) ? 'text-primary-700' : 'text-app-fg',
                        ]"
                        :title="node.employer.name"
                      >
                        {{ labelOf(node.employer) }}
                      </span>
                      <span
                        v-if="shortBadgeOf(node.employer)"
                        class="truncate text-[11px] font-normal text-app-muted"
                      >
                        {{ shortBadgeOf(node.employer) }}
                      </span>
                    </span>
                    <Check
                      v-if="isActive(node.employer.id)"
                      class="ml-auto h-4 w-4 shrink-0 text-primary"
                      aria-hidden="true"
                    />
                  </button>
                </MenuItem>
                <!-- Direct Workforce + the counterparties parented to THIS
                     subsidiary, one level deeper. -->
                <div class="oc-children">
                  <OrgChipDirectItem
                    :company-id="node.employer.id"
                    :selected="isDirectActive(node.employer.id)"
                    @select="onSelectDirect(node.employer)"
                  />
                  <template v-if="node.clients.length">
                    <p class="oc-sub-header" data-testid="org-chip-group-clients">
                      {{ t('v2.shell.org-chip.clients-projects') }}
                    </p>
                    <OrgChipCounterparty
                      v-for="entry in node.clients"
                      :key="entry.employer.id"
                      :entry="entry"
                      :active-id="activeId"
                      :expanded="expanded"
                      :max="MAX_VISIBLE_CHILDREN"
                      @select="onSelectEmployer"
                      @toggle="toggleExpanded"
                    />
                  </template>
                  <template v-if="node.vendors.length">
                    <p class="oc-sub-header" data-testid="org-chip-group-vendors">
                      {{ t('v2.shell.org-chip.vendors-projects') }}
                    </p>
                    <OrgChipCounterparty
                      v-for="entry in node.vendors"
                      :key="entry.employer.id"
                      :entry="entry"
                      :active-id="activeId"
                      :expanded="expanded"
                      :max="MAX_VISIBLE_CHILDREN"
                      @select="onSelectEmployer"
                      @toggle="toggleExpanded"
                    />
                  </template>
                </div>
              </template>
              <button
                v-if="hiddenCount(subsidiaryNodes, COMPANIES_KEY) > 0 || isExpanded(COMPANIES_KEY)"
                type="button"
                class="oc-show-more oc-show-more--child"
                :data-testid="`org-chip-show-more-${COMPANIES_KEY}`"
                @click="toggleExpanded(COMPANIES_KEY)"
              >
                {{
                  isExpanded(COMPANIES_KEY)
                    ? t('v2.shell.org-chip.show-less')
                    : t('v2.shell.org-chip.show-more', {
                        count: hiddenCount(subsidiaryNodes, COMPANIES_KEY),
                      })
                }}
              </button>

              <!-- The tenant employer's OWN clients (ParentId = tenant, or orphans). -->
              <template v-if="tenantClients.length">
                <p class="oc-sub-header" data-testid="org-chip-group-clients">
                  {{ t('v2.shell.org-chip.clients-projects') }}
                </p>
                <OrgChipCounterparty
                  v-for="entry in visibleTenantClients"
                  :key="entry.employer.id"
                  :entry="entry"
                  :active-id="activeId"
                  :expanded="expanded"
                  :max="MAX_VISIBLE_CHILDREN"
                  @select="onSelectEmployer"
                  @toggle="toggleExpanded"
                />
                <button
                  v-if="hiddenCount(tenantClients, CLIENTS_KEY) > 0 || isExpanded(CLIENTS_KEY)"
                  type="button"
                  class="oc-show-more oc-show-more--child"
                  :data-testid="`org-chip-show-more-${CLIENTS_KEY}`"
                  @click="toggleExpanded(CLIENTS_KEY)"
                >
                  {{
                    isExpanded(CLIENTS_KEY)
                      ? t('v2.shell.org-chip.show-less')
                      : t('v2.shell.org-chip.show-more', {
                          count: hiddenCount(tenantClients, CLIENTS_KEY),
                        })
                  }}
                </button>
              </template>

              <!-- The tenant employer's OWN vendors. -->
              <template v-if="tenantVendors.length">
                <p class="oc-sub-header" data-testid="org-chip-group-vendors">
                  {{ t('v2.shell.org-chip.vendors-projects') }}
                </p>
                <OrgChipCounterparty
                  v-for="entry in visibleTenantVendors"
                  :key="entry.employer.id"
                  :entry="entry"
                  :active-id="activeId"
                  :expanded="expanded"
                  :max="MAX_VISIBLE_CHILDREN"
                  @select="onSelectEmployer"
                  @toggle="toggleExpanded"
                />
                <button
                  v-if="hiddenCount(tenantVendors, VENDORS_KEY) > 0 || isExpanded(VENDORS_KEY)"
                  type="button"
                  class="oc-show-more oc-show-more--child"
                  :data-testid="`org-chip-show-more-${VENDORS_KEY}`"
                  @click="toggleExpanded(VENDORS_KEY)"
                >
                  {{
                    isExpanded(VENDORS_KEY)
                      ? t('v2.shell.org-chip.show-less')
                      : t('v2.shell.org-chip.show-more', {
                          count: hiddenCount(tenantVendors, VENDORS_KEY),
                        })
                  }}
                </button>
              </template>
            </div>
          </template>

          <!-- =====================================================================
               CASE B — no visible tenant employer (a restricted, non-admin
               principal): render the visible companies FLAT under a header. Any
               counterparties they administer render as their own flat groups.
               ===================================================================== -->
          <template v-else>
            <template v-if="companies.length > 0">
              <p class="oc-group-header" data-testid="org-chip-group-companies">
                {{ t('v2.shell.org-chip.companies') }}
              </p>
              <template v-for="company in visibleCompanies" :key="company.id">
                <MenuItem v-slot="{ active }">
                  <button
                    type="button"
                    :class="[
                      'group flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left transition-colors',
                      isActive(company.id) ? 'bg-primary-50' : active ? 'bg-slate-50' : '',
                    ]"
                    :data-testid="`org-chip-option-${company.id}`"
                    @click="onSelectEmployer(company)"
                  >
                    <span class="oc-icon" aria-hidden="true">
                      <Building2 class="h-4 w-4" />
                    </span>
                    <span class="flex min-w-0 flex-1 flex-col leading-tight">
                      <span
                        :class="[
                          'truncate text-md font-medium',
                          isActive(company.id) ? 'text-primary-700' : 'text-app-fg',
                        ]"
                        :title="company.name"
                      >
                        {{ labelOf(company) }}
                      </span>
                      <span
                        v-if="shortBadgeOf(company)"
                        class="truncate text-[11px] font-normal text-app-muted"
                      >
                        {{ shortBadgeOf(company) }}
                      </span>
                    </span>
                    <Check
                      v-if="isActive(company.id)"
                      class="ml-auto h-4 w-4 shrink-0 text-primary"
                      aria-hidden="true"
                    />
                  </button>
                </MenuItem>
                <!-- Direct Workforce of this company (proto L15626–15630). -->
                <div class="oc-children">
                  <OrgChipDirectItem
                    :company-id="company.id"
                    :selected="isDirectActive(company.id)"
                    @select="onSelectDirect(company)"
                  />
                </div>
              </template>
              <button
                v-if="hiddenCount(companies, COMPANIES_KEY) > 0 || isExpanded(COMPANIES_KEY)"
                type="button"
                class="oc-show-more px-3"
                :data-testid="`org-chip-show-more-${COMPANIES_KEY}`"
                @click="toggleExpanded(COMPANIES_KEY)"
              >
                {{
                  isExpanded(COMPANIES_KEY)
                    ? t('v2.shell.org-chip.show-less')
                    : t('v2.shell.org-chip.show-more', {
                        count: hiddenCount(companies, COMPANIES_KEY),
                      })
                }}
              </button>
            </template>

            <!-- Administered projects whose parent client/vendor isn't itself
                 visible — rendered flat, like companies, so a project-scoped
                 admin (or a legacy pre-ParentId project row) is still reachable. -->
            <template v-if="looseProjects.length > 0">
              <p class="oc-group-header" data-testid="org-chip-group-loose-projects">
                {{ t('v2.shell.org-chip.projects') }}
              </p>
              <MenuItem
                v-for="project in visibleLooseProjects"
                :key="project.id"
                v-slot="{ active }"
              >
                <button
                  type="button"
                  :class="[
                    'group flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left transition-colors',
                    isActive(project.id) ? 'bg-primary-50' : active ? 'bg-slate-50' : '',
                  ]"
                  :data-testid="`org-chip-option-${project.id}`"
                  @click="onSelectEmployer(project)"
                >
                  <span class="oc-icon" aria-hidden="true">
                    <Folder class="h-4 w-4" />
                  </span>
                  <span class="flex min-w-0 flex-1 flex-col leading-tight">
                    <span
                      :class="[
                        'truncate text-md font-medium',
                        isActive(project.id) ? 'text-primary-700' : 'text-app-fg',
                      ]"
                      :title="project.name"
                    >
                      {{ labelOf(project) }}
                    </span>
                    <span
                      v-if="shortBadgeOf(project)"
                      class="truncate text-[11px] font-normal text-app-muted"
                    >
                      {{ shortBadgeOf(project) }}
                    </span>
                  </span>
                  <Check
                    v-if="isActive(project.id)"
                    class="ml-auto h-4 w-4 shrink-0 text-primary"
                    aria-hidden="true"
                  />
                </button>
              </MenuItem>
              <button
                v-if="
                  hiddenCount(looseProjects, LOOSE_PROJECTS_KEY) > 0 ||
                  isExpanded(LOOSE_PROJECTS_KEY)
                "
                type="button"
                class="oc-show-more px-3"
                :data-testid="`org-chip-show-more-${LOOSE_PROJECTS_KEY}`"
                @click="toggleExpanded(LOOSE_PROJECTS_KEY)"
              >
                {{
                  isExpanded(LOOSE_PROJECTS_KEY)
                    ? t('v2.shell.org-chip.show-less')
                    : t('v2.shell.org-chip.show-more', {
                        count: hiddenCount(looseProjects, LOOSE_PROJECTS_KEY),
                      })
                }}
              </button>
            </template>

            <template v-if="clients.length > 0">
              <p class="oc-group-header" data-testid="org-chip-group-clients">
                {{ t('v2.shell.org-chip.clients-projects') }}
              </p>
              <template v-for="entry in visibleClients" :key="entry.employer.id">
                <MenuItem v-slot="{ active }">
                  <button
                    type="button"
                    :class="[
                      'group flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left transition-colors',
                      isActive(entry.employer.id) ? 'bg-primary-50' : active ? 'bg-slate-50' : '',
                    ]"
                    :data-testid="`org-chip-option-${entry.employer.id}`"
                    @click="onSelectEmployer(entry.employer)"
                  >
                    <span class="oc-icon" aria-hidden="true">
                      <Briefcase class="h-4 w-4" />
                    </span>
                    <span class="flex min-w-0 flex-1 flex-col leading-tight">
                      <span
                        :class="[
                          'truncate text-md font-medium',
                          isActive(entry.employer.id) ? 'text-primary-700' : 'text-app-fg',
                        ]"
                        :title="entry.employer.name"
                      >
                        {{ labelOf(entry.employer) }}
                      </span>
                      <span
                        v-if="shortBadgeOf(entry.employer)"
                        class="truncate text-[11px] font-normal text-app-muted"
                      >
                        {{ shortBadgeOf(entry.employer) }}
                      </span>
                    </span>
                    <Check
                      v-if="isActive(entry.employer.id)"
                      class="ml-auto h-4 w-4 shrink-0 text-primary"
                      aria-hidden="true"
                    />
                  </button>
                </MenuItem>
                <div class="oc-children">
                  <MenuItem
                    v-for="project in visibleSlice(entry.projects, entry.employer.id)"
                    :key="project.id"
                    v-slot="{ active }"
                  >
                    <button
                      type="button"
                      :class="[
                        'oc-child-row',
                        isActive(project.id) ? 'bg-primary-50' : active ? 'bg-slate-50' : '',
                      ]"
                      :data-testid="`org-chip-option-${project.id}`"
                      @click="onSelectEmployer(project)"
                    >
                      <span class="oc-icon oc-icon--sm" aria-hidden="true">
                        <Folder class="h-4 w-4" />
                      </span>
                      <span class="flex min-w-0 flex-1 flex-col leading-tight">
                        <span
                          :class="[
                            'truncate text-sm font-medium',
                            isActive(project.id) ? 'text-primary-700' : 'text-app-fg',
                          ]"
                        >
                          {{ labelOf(project) }}
                        </span>
                        <span
                          v-if="shortBadgeOf(project)"
                          class="truncate text-[11px] font-normal text-app-muted"
                        >
                          {{ shortBadgeOf(project) }}
                        </span>
                      </span>
                      <Check
                        v-if="isActive(project.id)"
                        class="ml-auto h-4 w-4 shrink-0 text-primary"
                        aria-hidden="true"
                      />
                    </button>
                  </MenuItem>
                </div>
              </template>
            </template>
          </template>

          <!-- + Create new company — pinned at the bottom for tenant admins.
               Opens the create-company drawer; closes the menu on click. -->
          <template v-if="canCreateCompany">
            <div class="my-1 border-t border-app-border-soft" aria-hidden="true" />
            <MenuItem v-slot="{ active }">
              <button
                type="button"
                :class="[
                  'group flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-md font-medium text-primary transition-colors',
                  active ? 'bg-primary-50' : '',
                ]"
                data-testid="org-chip-create-company"
                @click="openCreate"
              >
                <span class="oc-icon oc-icon--brand" aria-hidden="true">
                  <Plus class="h-4 w-4" />
                </span>
                {{ t('v2.shell.org-chip.create-company') }}
              </button>
            </MenuItem>
          </template>
        </template>
      </MenuItems>
    </transition>
  </Menu>

  <!-- Create-company drawer — unified EmployerForm pinned to COMPANY. -->
  <Drawer
    :open="createOpen"
    :title="t('v2.organization.company-form.drawer-title')"
    :subtitle="t('v2.organization.company-form.drawer-subtitle')"
    width="520px"
    @close="createOpen = false"
  >
    <EmployerForm
      v-if="createOpen"
      kind="COMPANY"
      :saving="saving"
      :parent-options="createParentOptions"
      :default-parent-id="createDefaultParentId"
      :server-error="createError"
      @submit="submitCreateCompany"
      @cancel="createOpen = false"
    />
  </Drawer>
</template>

<style scoped>
/*
 * Local chrome for the switcher dropdown. Colours read the proto `--oh-*`
 * token graph (never inlined hex) so they track the palette via `src/style.css`.
 * The dotted tree connector is a single local custom property so its visibility
 * can be tuned in one place.
 */
/* Tree connector colour — derived from the muted token, lifted well above the
 * near-invisible `--oh-border` so the dotted guides actually read. */
[data-testid='org-chip-menu'] {
  --oc-line: color-mix(in srgb, var(--oh-muted) 60%, transparent);
}

/* Uppercase section header (DIRECT, COMPANIES) at the top indent. */
.oc-group-header {
  padding: 0.5rem 0.75rem 0.25rem;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--oh-muted);
}
/* Nested sub-group header (CLIENTS ▸ PROJECTS, VENDORS ▸ PROJECTS) — sits inside
 * a company's child container, so it carries the indent from `.oc-children`. */
.oc-sub-header {
  padding: 0.4rem 0.5rem 0.2rem;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--oh-muted);
}

/* Square icon tile for top-level rows (Globe / Building2). */
.oc-icon {
  display: inline-flex;
  height: 2rem;
  width: 2rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  border: 1px solid var(--oh-border);
  background: var(--oh-surface);
  color: var(--oh-muted);
}
.oc-icon--brand {
  background: var(--oh-primary-50);
  color: var(--oh-primary);
}
/* Smaller, borderless icon for nested rows (subsidiaries / clients / projects). */
.oc-icon--sm {
  height: 1.5rem;
  width: 1.5rem;
  border: none;
  background: transparent;
}

/*
 * Dotted parent→child tree connector. Each `.oc-children` is a container that
 * indents its rows and draws a vertical dotted guide down its left edge; each
 * `.oc-child-row` inside draws a short horizontal dotted stub reaching the row,
 * like a file tree. Nesting `.oc-children` compounds the indent + guides, so the
 * same markup works at every depth (company → client → project).
 */
.oc-children {
  position: relative;
  margin-left: 1.15rem;
  padding-left: 0.85rem;
  border-left: 1.5px dotted var(--oc-line);
}
.oc-child-row {
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0.375rem;
  padding: 0.375rem 0.5rem;
  text-align: left;
  transition: background-color 150ms;
}
.oc-child-row::before {
  content: '';
  position: absolute;
  left: -0.85rem;
  top: 50%;
  width: 0.85rem;
  border-top: 1.5px dotted var(--oc-line);
}

/* "Show more (N)" / "Show less" affordance. */
.oc-show-more {
  display: block;
  width: 100%;
  padding-top: 0.25rem;
  padding-bottom: 0.4rem;
  text-align: left;
  font-size: 11px;
  font-weight: 500;
  color: var(--oh-primary);
  transition: color 150ms;
}
.oc-show-more:hover {
  color: var(--oh-primary-700);
}
.oc-show-more--child {
  padding-left: 0.5rem;
}
</style>
